import { AfterViewInit, Component, OnDestroy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { TranslateParser } from "@ngx-translate/core";
import { MainService } from "core/main.service";
import { NotificationService } from "core/notification.service";
import { LanguageService } from "core/translate/language.service";
import { StagesTranslateParser } from "core/translate/stages-translate.parser";
import { ApplicationState } from "core/volatile-application-state.model";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AppLoadService } from "app/app-load.service";
import { MaintenanceService } from "common/maintenance/maintenance.service";
import { ApplicationContextRepository } from "core/application-context.repository";
import { ConfirmMaintenanceComponent } from "common/maintenance/confirm-maintenance.component";
import MaintenancePushNotification = stages.core.maintenance.MaintenancePushNotification;

@Component({
	selector: "stages-main",
	templateUrl: "./main.component.html",
	styleUrls: ["./main.component.scss"],
})
export class MainComponent implements OnDestroy, AfterViewInit {
	private destroy$ = new Subject<boolean>();

	applicationState: ApplicationState;
	menuItems: unknown[] = [];
	dialogActive: boolean = false;
	searchOpen: boolean = false;
	bannerEnabled: boolean = false;
	sudoEnabled: boolean = false;
	private oldToolbarVisibleState: boolean = false;
	isDisclaimerEnabled = false;
	disclaimerVersion: string | null = null;

	constructor(
		private mainService: MainService,
		private route: ActivatedRoute,
		private languageService: LanguageService,
		private translateParser: TranslateParser,
		private notificationService: NotificationService,
		private appLoadService: AppLoadService,
		private maintenanceService: MaintenanceService,
		applicationContextRepository: ApplicationContextRepository,
	) {
		this.applicationState = mainService.applicationState;

		// update language setting on workspace change
		this.route.data.pipe(takeUntil(this.destroy$)).subscribe((data) => {
			(this.translateParser as StagesTranslateParser).setCurrentMessageScope(
				data.workspaceView.currentWorkspace.messageScope,
			);
			this.languageService.determineAndApplyLanguage(
				this.route.snapshot.paramMap.get("processVersion")!,
				data.applicationContext.preferredLanguage,
			);

			this.bannerEnabled = data.applicationContext.bannerEnabled;
			this.disclaimerVersion = data.applicationContext.disclaimerVersion;
		});
		applicationContextRepository.applicationContext$.pipe(takeUntil(this.destroy$)).subscribe((applicationContext) => {
			this.sudoEnabled = applicationContext.superUser && applicationContext.username !== "root";
			this.isDisclaimerEnabled = applicationContext.disclaimerEnabled;
		});

		this.mainService.menuItems$.pipe(takeUntil(this.destroy$)).subscribe((menuItems) => (this.menuItems = menuItems));
		this.notificationService.subscribe("system", {});
		this.notificationService.setHandlers("system", {
			MAINTENANCE_STARTED: (maintenanceInfo: MaintenancePushNotification) => {
				this.maintenanceService.notify(
					"administration.maintenance.pushNotification.start.title",
					"administration.maintenance.pushNotification.start.message",
					maintenanceInfo,
					"STARTED",
					ConfirmMaintenanceComponent,
					true,
				);
			},
			MAINTENANCE_ANNOUNCED: (maintenanceInfo: MaintenancePushNotification) => {
				this.maintenanceService.notify(
					"administration.maintenance.pushNotification.announce.title",
					"administration.maintenance.pushNotification.announce.message",
					maintenanceInfo,
					"ANNOUNCED",
					ConfirmMaintenanceComponent,
					true,
				);
			},
		});

		this.applicationState.isSearchOpen.pipe(takeUntil(this.destroy$)).subscribe((isSearchOpen: boolean) => {
			this.searchOpen = isSearchOpen;
		});
	}

	onChangeSudoMode(): void {
		this.mainService.openPopup(["user", "sudo"], this.route);
	}

	ngOnDestroy(): void {
		this.destroy$.next(true);
		this.destroy$.complete();

		this.notificationService.unsubscribe("system");
	}

	onDialogActivate(): void {
		this.dialogActive = true;
		this.oldToolbarVisibleState = this.applicationState.toolbarVisible;
		// timeout needed in order to come after ProcessViewComponent that also uses timeout
		setTimeout(() => {
			this.applicationState.toolbarVisible = false;
		});
	}

	onDialogDeactivate(): void {
		this.dialogActive = false;
		this.applicationState.toolbarVisible = this.oldToolbarVisibleState;
	}

	onPopupOpened($event: Event): void {
		this.mainService.popupOpenState$.next(true);
	}

	onPopupClosed($event: Event): void {
		this.mainService.popupOpenState$.next(false);
	}

	ngAfterViewInit(): void {
		this.appLoadService.resolveBootstrapProperties().then((properties) => {
			if (properties.maintenance.maintenanceMode === "ANNOUNCED") {
				this.maintenanceService.notify(
					"administration.maintenance.pushNotification.announce.title",
					"administration.maintenance.pushNotification.announce.message",
					properties.maintenance,
					"ANNOUNCED",
					ConfirmMaintenanceComponent,
				);
			} else if (properties.maintenance.maintenanceMode === "STARTED") {
				this.maintenanceService.notify(
					"administration.maintenance.pushNotification.start.title",
					"administration.maintenance.pushNotification.start.message",
					properties.maintenance,
					"STARTED",
					ConfirmMaintenanceComponent,
				);
			}
		});
	}
}
